<div mat-dialog-title class="dialog-title p-3 pr-5 clearfix">
  <h5>
    Accounts {{ dataSource.multiple ? '(' + dataSource.selection.selected.length + ')' : '' }}
  </h5>

  <button
    mat-icon-button
    aria-label="Close dialog"
    matTooltip="Close this dialog."
    (click)="onClose()"
    class="dialog-close-button">
    <mat-icon>close</mat-icon>
  </button>
</div>

<hr class="m-0 p-0">

<mat-dialog-content class="p-0">

  <!--
    Two separate jobs, so two tabs: picking an account you already belong to, and searching for a company
    you do not. Stacking both lists in one scroll produced two tables with near-identical headers, which
    read as one broken table.

    Tabs are only used for a single selection. In multiple-select mode there is no memberships list, so a
    tab strip would wrap one tab around the search for no reason.
  -->
  <ng-container *ngIf="!data['multiple']; else searchOnly">
    <mat-tab-group [(selectedIndex)]="selectedTabIndex">

      <mat-tab label="Your Accounts">
        <div class="p-3">
          <div class="d-flex justify-content-center p-3" *ngIf="loadingMyAccounts">
            <mat-progress-spinner [mode]="'indeterminate'" diameter="28"></mat-progress-spinner>
          </div>

          <ng-container *ngIf="!loadingMyAccounts">
            <p class="text-muted mb-3" *ngIf="myAccounts.length">
              You already have access to these. Choose one to set it as your employer.
            </p>

            <table mat-table [dataSource]="myAccounts" class="full-width" *ngIf="myAccounts.length">
              <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef>Account Name</th>
                <td mat-cell *matCellDef="let account">{{ account.name }}</td>
              </ng-container>

              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef [style.width.px]="120">Actions</th>
                <td mat-cell *matCellDef="let account">
                  <button mat-button (click)="onSelectSingle(account.id)"
                          matTooltip="Set {{ account.name }} as your employer.">
                    Select
                  </button>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="['name', 'actions']"></tr>
              <tr mat-row *matRowDef="let row; columns: ['name', 'actions']"></tr>
            </table>

            <div class="alert alert-info mb-0" *ngIf="!myAccounts.length">
              <mat-icon class="align-middle mr-2">info</mat-icon>
              You do not belong to any company accounts yet. Use the
              <b>Search For An Employer</b> tab to find yours.
            </div>
          </ng-container>
        </div>
      </mat-tab>

      <mat-tab label="Search For An Employer">
        <div class="p-3">
          <ng-container *ngTemplateOutlet="searchBlock"></ng-container>
        </div>
      </mat-tab>

    </mat-tab-group>
  </ng-container>

  <!-- Multiple-select callers get the search on its own, exactly as before. -->
  <ng-template #searchOnly>
    <div class="p-3">
      <ng-container *ngTemplateOutlet="searchBlock"></ng-container>
    </div>
  </ng-template>

  <!--
    The search, defined once and reused by both branches above so the two cannot drift apart.
    The search field lives here rather than in the dialog header because it only drives this list.
  -->
  <ng-template #searchBlock>
    <form class="form-inline mb-3" (ngSubmit)="dataSource.getData(true)">
      <input
        class="form-control mr-2 my-1"
        type="search"
        [(ngModel)]="dataSource.search"
        name="search"
        placeholder="Search Accounts"
        aria-label="Search Accounts" />

      <button mat-raised-button color="secondary" class="mr-2 my-1" type="submit">
        <mat-icon>search</mat-icon> Search
      </button>

      <button
        mat-raised-button
        color="primary"
        type="button"
        class="my-1"
        (click)="onSelectSingle(null)"
        *ngIf="onCanCreateNewAccount()">
        <mat-icon>add</mat-icon> New Account
      </button>
    </form>

    <table mat-table [dataSource]="dataSource" matSort matSortActive="name" matSortDirection="asc" matSortDisableClear class="full-width">

      <ng-container matColumnDef="select">
        <th mat-header-cell *matHeaderCellDef [style.width.px]="65">
          <mat-checkbox
            (change)="$event ? dataSource.selectAllToggle() : null"
            [checked]="dataSource.selection.hasValue() && dataSource.isAllSelected()"
            [indeterminate]="dataSource.selection.hasValue() && !dataSource.isAllSelected()">
          </mat-checkbox>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            (click)="$event.stopPropagation()"
            (change)="$event ? dataSource.selection.toggle(row.id) : null"
            [checked]="dataSource.selection.isSelected(row.id)">
          </mat-checkbox>
        </td>
      </ng-container>

      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Account Name</th>
        <td mat-cell *matCellDef="let element">{{ element.name }}</td>
      </ng-container>

      <ng-container matColumnDef="date_created">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Date Joined</th>
        <td mat-cell *matCellDef="let element">{{ (element.date_created * 1000) | timezoneConvertMedium }}</td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef [style.width.px]="150">Actions</th>
        <td mat-cell *matCellDef="let element">
          <button
            mat-button
            (click)="onSelectSingle(element.id)"
            *ngIf="!this.dataSource.isSelected(element.id); else alreadySelected">
            Select
          </button>
          <ng-template #alreadySelected>
            <span class="text-muted">Already Selected</span>
          </ng-template>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns" (click)="dataSource.toggleRowSelection(row.id)"></tr>
    </table>

    <p class="text-muted mt-3 mb-0">
      <i>
        Enter your employer's name above and click search. If no results are shown, enter their full name
        and click the "New Account" button to add them.
      </i>
    </p>
  </ng-template>

</mat-dialog-content>

<hr class="m-0 p-0">

<mat-dialog-actions class="d-flex justify-content-end px-2 py-1">
  <!--
    Paginates the SEARCH results only, so it is hidden on the Your Accounts tab where it would appear to
    describe a list it has nothing to do with.

    [hidden] rather than *ngIf on purpose: ngAfterViewInit() wires this paginator up through @ViewChild, and
    removing it from the DOM would leave that reference undefined and throw on open.
  -->
  <mat-paginator
    class="my-2 ml-1 mr-2"
    #paginator
    [hidden]="!data['multiple'] && selectedTabIndex !== 1"
    [length]="dataSource?.total | async"
    [pageSize]="dataSource.limit"
    [pageSizeOptions]="dataSource.pageSizeOptions"
    showFirstLastButtons>
  </mat-paginator>

  <button
    mat-raised-button
    class="my-2 ml-1 mr-2"
    color="primary"
    (click)="onSelectMultiple()"
    *ngIf="data['multiple']">
    Select
  </button>

  <button
    mat-raised-button
    class="my-2 ml-1 mr-2"
    color="secondary"
    (click)="onDeselectAllRecords()"
    *ngIf="dataSource.selection.selected.length > paginator.pageSize">
    Deselect All
  </button>
</mat-dialog-actions>
